import { useCallback, useEffect, useMemo } from "react";
import { RefCallBack, useController, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { useIssueFormatter } from "../../../application/hooks/useIssueFormatter";
import { ExtraCategorizationVO } from "../../../domain/valueObjects/ExtraCategorizationVO";
import {
  mapExtraCategorizationToForm,
  mapExtraCategoryFormToDomain,
} from "../../mappers/extra-category-form.mapper";
import { ExtraCategoryFormValues } from "../../types/ExtraCategoryFormTypes";
import { extraCategoryInputsMessages as messages } from "./ExtraCategoryInputs.messages";
import {
  buildCategoryOptions,
  buildVehicleTypeOptions,
  ExtraCategoryField,
  isCategoryEnabled,
  NO_VEHICLE_TYPE,
  parseCategoryOption,
  parseVehicleTypeOption,
  SelectOption,
  showsVehicleFields,
  validateVehicleType,
} from "./extraCategoryInputs.viewModel";

interface FieldModel {
  readonly id: string;
  readonly name: ExtraCategoryField;
  readonly disabled: boolean;
  readonly error?: string;
  readonly inputRef: RefCallBack;
  readonly onBlur: () => void;
}

export interface SelectFieldModel extends FieldModel {
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
}

export interface CheckboxFieldModel extends FieldModel {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

export interface ExtraCategoryInputsModel {
  readonly category: SelectFieldModel & { readonly showUnsupportedHelp: boolean };
  /** `undefined` while the vehicle fields are hidden (category other than Vehicle). */
  readonly vehicleType?: SelectFieldModel & { readonly placeholder: string };
  readonly requestLicensePlate?: CheckboxFieldModel;
}

const CATEGORY_FIELDS: ExtraCategoryField[] = ["category", "vehicleType", "requestLicensePlate"];

/** Writes a categorization into the three form fields, as a user edit. */
function useSetCategorization(): (categorization: ExtraCategorizationVO) => void {
  const { setValue, clearErrors } = useFormContext<ExtraCategoryFormValues>();

  return useCallback(
    (categorization: ExtraCategorizationVO) => {
      const fields = mapExtraCategorizationToForm(categorization);
      setValue("category", fields.category, { shouldDirty: true });
      setValue("vehicleType", fields.vehicleType, { shouldDirty: true });
      setValue("requestLicensePlate", fields.requestLicensePlate, { shouldDirty: true });
      clearErrors(CATEGORY_FIELDS);
    },
    [setValue, clearErrors]
  );
}

/**
 * A type that does not support categories resets the category fields; switching
 * between the two supported types keeps them. Idempotent, so it also covers loaded data.
 */
export function useExtraCategoryTypeReset(): void {
  const { control, getValues } = useFormContext<ExtraCategoryFormValues>();
  const type = useWatch({ control, name: "type" });
  const setCategorization = useSetCategorization();

  useEffect(() => {
    const current = mapExtraCategoryFormToDomain(getValues());
    const next = current.forExtraType(type);
    if (!next.equals(current)) {
      setCategorization(next);
    }
  }, [getValues, setCategorization, type]);
}

/** State and actions of the extra page category fields. */
export function useExtraCategoryInputs(disabled: boolean): ExtraCategoryInputsModel {
  const intl = useIntl();
  const formatter = useIssueFormatter();
  const { control, getValues } = useFormContext<ExtraCategoryFormValues>();
  const type = useWatch({ control, name: "type" });
  const setCategorization = useSetCategorization();

  useExtraCategoryTypeReset();

  const category = useController({ control, name: "category" });
  const vehicleType = useController({
    control,
    name: "vehicleType",
    rules: { validate: (_value, values) => validateVehicleType(values, formatter) },
  });
  const requestLicensePlate = useController({ control, name: "requestLicensePlate" });

  const categoryOptions = useMemo(() => buildCategoryOptions(intl), [intl]);
  const vehicleTypeOptions = useMemo(() => buildVehicleTypeOptions(intl), [intl]);

  const onCategoryChange = useCallback(
    (value: string) =>
      setCategorization(
        mapExtraCategoryFormToDomain(getValues()).withCategory(parseCategoryOption(value))
      ),
    [getValues, setCategorization]
  );

  const enabled = isCategoryEnabled(type);

  return {
    category: {
      id: "extraCategory",
      name: "category",
      value: category.field.value,
      options: categoryOptions,
      disabled: disabled || !enabled,
      showUnsupportedHelp: !enabled,
      error: category.fieldState.error?.message,
      inputRef: category.field.ref,
      onBlur: category.field.onBlur,
      onChange: onCategoryChange,
    },
    ...(showsVehicleFields(category.field.value) && {
      vehicleType: {
        id: "extraVehicleType",
        name: "vehicleType",
        value: vehicleType.field.value ?? NO_VEHICLE_TYPE,
        options: vehicleTypeOptions,
        placeholder: intl.formatMessage(messages.vehicleTypePlaceholder),
        disabled,
        error: vehicleType.fieldState.error?.message,
        inputRef: vehicleType.field.ref,
        onBlur: vehicleType.field.onBlur,
        onChange: value => vehicleType.field.onChange(parseVehicleTypeOption(value)),
      },
      requestLicensePlate: {
        id: "extraRequestLicensePlate",
        name: "requestLicensePlate",
        checked: requestLicensePlate.field.value,
        disabled,
        error: requestLicensePlate.fieldState.error?.message,
        inputRef: requestLicensePlate.field.ref,
        onBlur: requestLicensePlate.field.onBlur,
        onChange: requestLicensePlate.field.onChange,
      },
    }),
  };
}
